import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { usePendingCompanyInvitation } from '@/hooks/usePendingCompanyInvitation';
import { describeJoinError } from '@/lib/invitationErrors';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';

export const FEATURE_META: FeatureMeta = {
  id: 'pending-invitation-banner',
  label: 'Team Invitation Banner',
  description: 'Persistent banner shown to a signed-in user who has a live invitation to join another company, with join, request-to-join, and decline actions.',
  triggerType: 'panel',
  personas: ['buyer', 'seller', 'buyer_seller'],
};

export default function PendingInvitationBanner() {
  useFeatureRegistration(FEATURE_META);
  const { invitation, refresh } = usePendingCompanyInvitation();
  const [busy, setBusy] = useState<'join' | 'decline' | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  if (!invitation || dismissed) return null;

  const join = async () => {
    setBusy('join');
    try {
      if (invitation.can_join_directly) {
        const { error } = await supabase.rpc('accept_company_invitation', {
          p_company_id: invitation.company_id,
          p_invitation_id: invitation.id,
        });
        if (error) throw error;
        toast.success(`Welcome to ${invitation.company_name}`, {
          description: "You're now part of the team.",
        });
        queryClient.clear();
        navigate('/my-listings', { replace: true });
        window.location.reload();
        return;
      }

      const { data, error } = await supabase.functions.invoke('submit-join-request', {
        body: { company_id: invitation.company_id, message: 'Accepting a team invitation' },
      });
      if (error) throw error;
      if (data?.error === 'duplicate') {
        toast.info('Request already sent', {
          description: `An admin at ${invitation.company_name} still needs to approve it.`,
        });
      } else {
        toast.success('Request sent', {
          description: `An admin at ${invitation.company_name} will move you onto the team.`,
        });
      }
      await refresh();
    } catch (err) {
      const { title, description } = describeJoinError(err);
      toast.error(title, { description });
    } finally {
      setBusy(null);
    }
  };

  const decline = async () => {
    setBusy('decline');
    try {
      const { error } = await supabase.rpc('decline_company_invitation', {
        p_invitation_id: invitation.id,
      });
      if (error) throw error;
      toast.success('Invitation declined', {
        description: `${invitation.company_name} has been notified you're staying in your own workspace.`,
      });
      setDismissed(true);
      await refresh();
    } catch {
      toast.error("Couldn't decline the invitation", { description: 'Please try again.' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border-b border-primary/30 bg-primary/5">
      <div className="container-wide flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex circle-8 shrink-0 items-center justify-center bg-primary/10 text-primary">
            <Users className="h-4 w-4" />
          </div>
          <div className="text-sm">
            <p className="font-medium text-foreground">
              {invitation.inviter_name
                ? `${invitation.inviter_name} invited you to join ${invitation.company_name}`
                : `You've been invited to join ${invitation.company_name}`}
            </p>
            <p className="text-muted-foreground">
              {invitation.can_join_directly
                ? 'Joining moves your saved searches and alerts onto the team and retires your solo workspace.'
                : `Your workspace ${invitation.current_company_name ?? ''} already holds data, so an admin at ${invitation.company_name} has to move you.`}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" onClick={join} disabled={busy !== null}>
            {busy === 'join' && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {invitation.can_join_directly ? `Join ${invitation.company_name}` : 'Request to join'}
          </Button>
          <Button size="sm" variant="ghost" onClick={decline} disabled={busy !== null}>
            {busy === 'decline' && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Stay in my workspace
          </Button>
        </div>
      </div>
    </div>
  );
}